/**
 * ImageCropper.tsx — v0.4.9.3.4c8
 *
 * Library: react-image-crop (the cropper rectangle is the affordance, the
 * image is the background — what every phone plate-reading app actually
 * does).
 *
 * v0.4.9.3.4c8 — Removed forced minimum upscaling.
 *
 *   The output-size label and the underlying crop logic no longer force
 *   a 960px minimum on the long edge. Tight crops stay small and sharp,
 *   which dramatically improves OCR accuracy. We still downscale if the
 *   crop exceeds 1280px.
 *
 * v0.4.9.3.4c7 — the c6 self-bug:
 *
 *   c6 computed the DOM→natural conversion factor inside `getCroppedImg`
 *   as `naturalWidth / image.width`. That looks right, but the image
 *   being measured is a DETACHED image (`new Image()` inside the
 *   `loadImage` helper), not the rendered `<img>` in the cropper.
 *
 *   Fix: the component passes the actual rendered DOM dimensions of the
 *   `<img>` (`imgRef.current.clientWidth`, `imgRef.current.clientHeight`)
 *   to `getCroppedImg`. Those reflect CSS sizing / viewport constraints.
 *
 *   Also, the cropper container's `overflow-auto` was removed. Combined
 *   with `flex items-center justify-center`, `overflow-auto` was
 *   scrolling the image out of sync with the crop selection — the "shift"
 *   the user saw.
 */
import React, { useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Check, X, RotateCcw } from 'lucide-react';
import { getCroppedImg } from '../lib/cropImage';

export type CropperAspect = '1:1' | '3:1' | 'free';

interface Props {
  imageDataUrl: string;
  aspect: CropperAspect;
  onConfirm: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

const MAX_OUTPUT = 1280;  // long-edge ceiling on the emitted crop (matches server _MAX_DIM)

export default function ImageCropper({ imageDataUrl, aspect, onConfirm, onCancel }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = useState<Crop>(() => initialCrop(aspect));
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aspectMode, setAspectMode] = useState<CropperAspect>(aspect);
  const aspectRatio =
    aspectMode === 'free' ? undefined :
    aspectMode === '1:1' ? 1 :
    3;

  function handleComplete(c: PixelCrop) {
    setCompletedCrop(c);
    if (imgRef.current) {
      // eslint-disable-next-line no-console
      console.log(
        `[ImageCropper] onComplete pixelCrop: x=${c.x.toFixed(0)} y=${c.y.toFixed(0)} w=${c.width.toFixed(0)} h=${c.height.toFixed(0)} (px)`,
        `image DOM=${imgRef.current.clientWidth}x${imgRef.current.clientHeight} natural=${imgRef.current.naturalWidth}x${imgRef.current.naturalHeight}`
      );
    }
  }

  async function handleSave() {
    if (!completedCrop || completedCrop.width === 0 || completedCrop.height === 0) return;
    if (!imgRef.current) {
      setError('Image reference lost');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // v0.4.9.3.4c7 — pass the actual RENDERED DOM dimensions of the
      // <img>. NOT `img.width` / `img.height` — those are the HTML
      // attribute values, which return 0 when the <img> doesn't have an
      // explicit `width="..."` attribute (which ours doesn't — only CSS
      // `maxWidth: 100%` / `maxHeight: 100%`). The rendered CSS size is
      // `clientWidth` / `clientHeight`. We need those for the DOM→natural
      // scaling factor.
      const renderedWidth = imgRef.current.clientWidth;
      const renderedHeight = imgRef.current.clientHeight;
      // eslint-disable-next-line no-console
      console.log(`[ImageCropper] save: rendered DOM=${renderedWidth}x${renderedHeight} natural=${imgRef.current.naturalWidth}x${imgRef.current.naturalHeight}`);
      const blobUrl = await getCroppedImg(imageDataUrl, completedCrop, renderedWidth, renderedHeight);
      const dataUrl = await blobUrlToDataUrl(blobUrl);
      URL.revokeObjectURL(blobUrl);
      onConfirm(dataUrl);
    } catch (err: any) {
      setError(err?.message || 'Failed to crop image');
    } finally {
      setSaving(false);
    }
  }

  async function blobUrlToDataUrl(blobUrl: string): Promise<string> {
    const resp = await fetch(blobUrl);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  function reset() {
    setCrop(initialCrop(aspectMode));
    setCompletedCrop(null);
  }

  function switchAspect(next: CropperAspect) {
    setAspectMode(next);
    setCrop(initialCrop(next));
    setCompletedCrop(null);
  }

  // Mirror getCroppedImg's resize logic so the label matches the actual
  // emitted pixels. We only downscale if the crop is larger than MAX_OUTPUT.
  const outputSize = (() => {
    if (!completedCrop || completedCrop.width === 0 || completedCrop.height === 0) return '';
    if (!imgRef.current) return `${Math.round(completedCrop.width)}×${Math.round(completedCrop.height)}px`;
    
    const dw = imgRef.current.clientWidth || imgRef.current.naturalWidth;
    const dh = imgRef.current.clientHeight || imgRef.current.naturalHeight;
    const factorX = imgRef.current.naturalWidth / dw;
    const factorY = imgRef.current.naturalHeight / dh;
    
    const natWidth = completedCrop.width * factorX;
    const natHeight = completedCrop.height * factorY;
    const long = Math.max(natWidth, natHeight);
    
    // Only downscale if too large. Do not upscale small crops.
    const s = long > MAX_OUTPUT ? MAX_OUTPUT / long : 1;
    return `${Math.round(natWidth * s)}×${Math.round(natHeight * s)}px`;
  })();

  const headerLabel =
    aspectMode === '1:1' ? 'Fit the face in the box' :
    aspectMode === '3:1' ? 'Fit the plate in the box' :
    'Drag corners to select';

  const saveDisabled =
    saving ||
    !completedCrop ||
    completedCrop.width === 0 ||
    completedCrop.height === 0;

  return (
    <div className="fixed inset-0 z-[70] bg-background text-foreground flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-black/60 backdrop-blur">
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-2 hover:bg-white/10 rounded-full"
        >
          <X className="w-5 h-5" />
          <span className="text-sm">Cancel</span>
        </button>
        <div className="text-sm font-medium">{headerLabel}</div>
        <button
          onClick={handleSave}
          disabled={saveDisabled}
          className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium disabled:opacity-50"
        >
          <Check className="w-4 h-4" />
          <span className="text-sm">{saving ? 'Saving…' : 'Save'}</span>
        </button>
      </div>

      {/* Aspect toggle */}
      <div className="flex items-center justify-center gap-2 p-2 bg-black/60 border-b border-white/5">
        {(['1:1', '3:1', 'free'] as CropperAspect[]).map((a) => (
          <button
            key={a}
            onClick={() => switchAspect(a)}
            className={
              'text-xs px-3 py-1 rounded-full transition ' +
              (aspectMode === a
                ? 'bg-primary text-primary-foreground'
                : 'bg-white/10 text-white hover:bg-white/20')
            }
          >
            {a === 'free' ? 'Free' : a}
          </button>
        ))}
      </div>

      {/* Cropper area */}
      <div className="ric-dark flex-1 relative bg-black flex items-center justify-center p-4">
        <ReactCrop
          crop={crop}
          onChange={(_crop, percentCrop) => setCrop(percentCrop)}
          onComplete={handleComplete}
          aspect={aspectRatio}
          keepSelection
          minWidth={40}
          minHeight={40}
        >
          <img
            ref={imgRef}
            src={imageDataUrl}
            alt="crop source"
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              userSelect: 'none',
            }}
          />
        </ReactCrop>
        {completedCrop && outputSize && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
            {outputSize}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-3 flex flex-col items-center gap-2 bg-black/60 backdrop-blur">
        <div className="w-full flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center gap-1 px-3"
            aria-label="Reset crop"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-xs">Reset</span>
          </button>
        </div>
        <div className="text-white/60 text-xs">
          Drag the corners of the box to fit the plate
        </div>
        {error && <div className="text-red-300 text-xs">{error}</div>}
      </div>
    </div>
  );
}

/** Percent-based so the crop survives image-size changes from viewport. */
function initialCrop(aspect: CropperAspect): Crop {
  if (aspect === 'free') {
    return { unit: '%', x: 25, y: 25, width: 50, height: 50 };
  }
  if (aspect === '1:1') {
    return { unit: '%', x: 25, y: 25, width: 50, height: 50 };
  }
  return { unit: '%', x: 10, y: 35, width: 80, height: 30 };
}